import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { apiUrl, getErrorMessage } from '../utils/api';

function Security() {
  const navigate = useNavigate();
  const [pending] = useState(() => {
    try { return JSON.parse(localStorage.getItem('twoFactorPending') || 'null'); } catch { return null; }
  });
  const [setup, setSetup] = useState(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const info = JSON.parse(localStorage.getItem('userInfo') || 'null');
  const token = pending?.setupToken || info?.token;
  const config = useMemo(() => (token ? { headers: { Authorization: `Bearer ${token}` } } : null), [token]);
  const isAdmin = info?.role === 'admin';
  const isOwner = info?.businessRoles?.some((membership) => membership.role === 'owner');

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    if (pending?.setupToken) {
      axios.get(apiUrl('/api/auth/2fa/setup'), config)
        .then(({ data }) => setSetup(data))
        .catch((err) => setError(getErrorMessage(err, 'Could not start two-factor setup.')));
    }
  }, [config, navigate, pending?.setupToken, token]);

  const startSetup = async () => {
    setBusy(true); setError(''); setMessage('');
    try {
      const { data } = await axios.post(apiUrl('/api/auth/2fa/setup'), {}, config);
      setSetup(data); setSetupComplete(false); setCode('');
    } catch (err) { setError(getErrorMessage(err, 'Could not start two-factor setup.')); }
    finally { setBusy(false); }
  };

  const confirm = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    const endpoint = pending?.setupToken ? '/api/auth/2fa/setup/confirm' : '/api/auth/2fa/enable/confirm';
    try {
      const { data } = await axios.post(apiUrl(endpoint), { code }, config);
      localStorage.removeItem('twoFactorPending');
      localStorage.setItem('userInfo', JSON.stringify(data));
      window.dispatchEvent(new Event('authchange'));
      setBackupCodes(data.backupCodes || []); setSetupComplete(true); setMessage('Two-factor authentication is enabled. Save your backup codes securely.');
    } catch (err) { setError(getErrorMessage(err, 'The authenticator code was not accepted.')); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    const entered = window.prompt('Enter your current authenticator code');
    if (!entered) return;
    setError('');
    try {
      const { data } = await axios.post(apiUrl('/api/auth/2fa/backup-codes'), { code: entered }, config);
      setBackupCodes(data.backupCodes || []); setMessage('New backup codes generated. Previous backup codes no longer work.');
    } catch (err) { setError(getErrorMessage(err, 'Could not regenerate backup codes.')); }
  };

  const disable = async (event) => {
    event.preventDefault(); setBusy(true); setError(''); setMessage('');
    try {
      const { data } = await axios.post(apiUrl('/api/auth/2fa/disable'), { password: disablePassword, code: disableCode }, config);
      localStorage.setItem('userInfo', JSON.stringify(data));
      window.dispatchEvent(new Event('authchange'));
      setBackupCodes([]); setDisablePassword(''); setDisableCode('');
      setMessage('Two-factor authentication is turned off for this account.');
    } catch (err) { setError(getErrorMessage(err, 'Could not turn off two-factor authentication.')); }
    finally { setBusy(false); }
  };

  if (!info && !pending) return null;
  const showSetup = Boolean(setup) && !setupComplete;

  return (
    <div className="page auth-page">
      <section className="panel auth-card security-card">
        <p className="eyebrow">Account security</p>
        <h1>Two-factor authentication</h1>
        {showSetup ? (
          <>
            <p className="muted-text">Scan this QR code with an authenticator app.</p>
            {setup.qrCode && <img className="twofactor-qr" src={setup.qrCode} alt="Authenticator setup QR code" />}
            {setup.secret && <p className="muted-text">Manual setup key: <code>{setup.secret}</code></p>}
            <form onSubmit={confirm}>
              <input inputMode="numeric" autoComplete="one-time-code" placeholder="Enter the six-digit code" value={code} onChange={(event) => setCode(event.target.value)} required />
              <button className="button primary" disabled={busy}>{busy ? 'Confirming…' : 'Confirm setup'}</button>
            </form>
          </>
        ) : info?.twoFactorEnabled ? (
          <>
            <p className="muted-text">Authenticator-app protection is enabled for this account.</p>
            <button className="button ghost" onClick={regenerate}>Regenerate backup codes</button>
            {!isAdmin && (
              <form className="security-disable-form" onSubmit={disable}>
                <h2>Turn off two-factor authentication</h2>
                <p className="muted-text">Enter your password and a current authenticator code to disable it.</p>
                <input type="password" autoComplete="current-password" placeholder="Current password" value={disablePassword} onChange={(event) => setDisablePassword(event.target.value)} required />
                <input inputMode="numeric" autoComplete="one-time-code" placeholder="Authenticator code" value={disableCode} onChange={(event) => setDisableCode(event.target.value)} required />
                <button type="submit" className="button ghost" disabled={busy}>{busy ? 'Updating…' : 'Turn off two-factor authentication'}</button>
              </form>
            )}
          </>
        ) : (
          <>
            <p className="muted-text">
              {isAdmin
                ? 'Two-factor authentication is required for platform administrators. Complete setup to protect this account.'
                : isOwner
                  ? 'Two-factor authentication is optional for business owners. You can continue without it or enable an authenticator app for extra protection.'
                  : 'Two-factor authentication is optional. Enable an authenticator app for extra account protection.'}
            </p>
            <button className="button primary" onClick={startSetup} disabled={busy}>{busy ? 'Preparing…' : 'Set up two-factor authentication'}</button>
          </>
        )}
        {pending?.setupToken && !setup && !error && <p className="muted-text">Preparing authenticator setup…</p>}
        {backupCodes.length > 0 && <div className="backup-codes"><h3>Save these backup codes</h3><p className="muted-text">Each code can be used once if you lose access to your authenticator.</p><code>{backupCodes.join('\n')}</code></div>}
        {message && <p className="success-message">{message}</p>}
        {error && <p className="error-message">{error}</p>}
        <p className="muted-text"><Link to="/dashboard">Back to dashboard</Link></p>
      </section>
    </div>
  );
}

export default Security;
